import * as React from 'react'
import { ReactElement } from 'react'
import {
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Overflow,
    OverflowItem,
    OverflowItemProps,
    Toolbar,
    ToolbarButton,
    ToolbarButtonProps,
    ToolbarProps,
    Tooltip,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components'
import type { ObserveOptions } from '@fluentui/priority-overflow'
import { useTranslation } from 'react-i18next'
import { bundleIcon, MoreVerticalFilled, MoreVerticalRegular } from '@fluentui/react-icons'

const MoreIcon = bundleIcon(MoreVerticalFilled, MoreVerticalRegular)

export type HammockToolbarProps = Pick<ToolbarProps, 'className' | 'size'>
export type HammockToolbarButtonProps = Pick<ToolbarButtonProps, 'appearance' | 'className'>

export type HammockToolbarButtonData = Omit<ToolbarButtonProps, 'onClick'> & {
    titleKey?: string
    onClick?: () => void
    filter?: () => boolean
    render?: (button: HammockToolbarButtonData, buttonIndex: number) => ReactElement
}

type OverflowToolbarPanel = Partial<ObserveOptions> & {
    containerClassName?: string
    toolbarProps: HammockToolbarProps
    toolbarButtonProps: HammockToolbarButtonProps
    buttons: HammockToolbarButtonData[]
    overflowButtonLabelKey: string
}

type OverflowMenuItemProps = OverflowToolbarPanel &
    Pick<OverflowItemProps, 'id'> & {
        value: HammockToolbarButtonData
    }

function OverflowMenuItem(props: OverflowMenuItemProps): ReactElement {
    const { t: translate } = useTranslation()
    const isVisible = useIsOverflowItemVisible(props.id)

    if (isVisible) {
        return null
    }

    return (
        <MenuItem
            onClick={() => {
                props.value.onClick?.()
            }}
            icon={props.value.icon}
        >
            {translate(props.value.titleKey)}
        </MenuItem>
    )
}

function OverflowMenu(props: OverflowToolbarPanel): ReactElement {
    const { t: translate } = useTranslation()
    const { ref, overflowCount, isOverflowing } = useOverflowMenu<HTMLButtonElement>()

    if (!isOverflowing) {
        return null
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={`+${translate(props.overflowButtonLabelKey, { count: overflowCount })}`}>
                    <ToolbarButton key={'-1'} ref={ref} {...props.toolbarButtonProps} icon={<MoreIcon />} value={'more'} />
                </Tooltip>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    {props.buttons.map((value, index) => (
                        <OverflowMenuItem key={index} id={`${index}`} value={value} {...props} />
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

export default function OverflowToolbarPanel(props: OverflowToolbarPanel): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <Overflow minimumVisible={props.minimumVisible ?? 3}>
            <div className={props.containerClassName}>
                <Toolbar {...props.toolbarProps}>
                    <>
                        {props.buttons?.map((button, index) => (
                            <OverflowItem key={index} id={`${index}`}>
                                {button.render ? (
                                    button.render(button, index)
                                ) : (
                                    <Tooltip key={index} relationship={'label'} content={translate(button.titleKey)}>
                                        <ToolbarButton key={index} {...props.toolbarButtonProps} icon={button.icon} onClick={button.onClick} />
                                    </Tooltip>
                                )}
                            </OverflowItem>
                        ))}
                        <OverflowMenu {...props} />
                    </>
                </Toolbar>
            </div>
        </Overflow>
    )
}
